import React, { useMemo, useRef, useState } from "react";
import { ModelListMenu, ModelMark, PickerTrigger } from "../picker";
import { TableCheckRow } from "../table-check-row";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { Button } from "@/components/common/Button";
import { SegmentedControl } from "@/components/common/SegmentedControl";
import { TooltipButton } from "@/components/common/TooltipButton";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { plural } from "@/components/common/setUtils";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import type { TableLink } from "@/lib/api/apps/apps";
import {
  useApps,
  useDesignApp,
  useModelTables,
  useTableLinks,
} from "@/lib/api/apps/hooks";
import type {
  DesignApp,
  DesignSummary,
  LendAccess,
} from "@/lib/api/apps/types";
import { DraftGraph, type EmbedModel } from "../design/draft-graph";
import { useEditableModels } from "../design/target-picker";

/** A table lent to the app, as the setup shows it. */
export interface LentTable {
  table_id: string;
  name: string;
  model: string | null;
  access: LendAccess;
}

type Source = { kind: "space" | "design"; id: string };

/** The tables a design applied, by the ids its applied version recorded. */
export function appliedTables(
  design: DesignApp,
): { id: string; name: string }[] {
  const version = design.versions.find((v) => v.n === design.applied_version);
  return (version?.tables ?? []).flatMap((t) =>
    t.table_id ? [{ id: t.table_id, name: t.name }] : [],
  );
}

const menuHead = (label: string) => (
  <span
    className="px-2 py-1 font-mono text-fg-muted uppercase select-none"
    style={{ fontSize: "var(--font-size-xx-small)", letterSpacing: "0.1em" }}
  >
    {label}
  </span>
);

/** A space or a design app, then a model, then its tables, then Add. A
 *  design's tables are lent to write: it made them to be filled; any other
 *  table to read, until the person says otherwise. */
export function TablePicker({
  orgId,
  lent,
  onAdd,
  onCancel,
}: {
  orgId: string | null;
  lent: LentTable[];
  onAdd: (tables: LentTable[]) => void;
  onCancel?: () => void;
}) {
  // Lending takes the right to share; a model's owner holds it.
  const models = useEditableModels().filter((m) => m.relation === "Owner");
  const spaceById = useOrgContextStore((s) => s.spaceById);
  const designs = (useApps(orgId).data ?? []).filter(
    (a): a is DesignSummary => a.kind === "design",
  );
  const [source, setSource] = useState<Source | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [menu, setMenu] = useState<"source" | "model" | null>(null);
  const sourceRef = useRef<HTMLButtonElement>(null);
  const modelRef = useRef<HTMLButtonElement>(null);
  const fromDesign = source?.kind === "design";
  const design = useDesignApp(fromDesign ? (source?.id ?? null) : null).data;
  const model = fromDesign ? (design?.target_model_id ?? null) : modelId;
  const tables = useModelTables(orgId, model).data;
  const applied = useMemo(
    () => new Set(design ? appliedTables(design).map((t) => t.id) : []),
    [design],
  );
  const rows = (tables ?? []).filter(
    (t) => !fromDesign || applied.has(t.table_id),
  );
  const spaces = [...new Set(models.map((m) => m.space_id as string))].flatMap(
    (id) => spaceById(id) ?? [],
  );
  const space = source?.kind === "space" ? spaceById(source.id) : undefined;
  const modelOf = models.find((m) => m.model_id === model);
  const isLent = (id: string) => lent.some((t) => t.table_id === id);
  const ticked = (id: string) => isLent(id) || (ticks[id] ?? fromDesign);
  const adding = rows.filter((t) => ticked(t.table_id) && !isLent(t.table_id));
  const pickSource = (next: Source) => {
    setSource(next);
    setModelId(null);
    setTicks({});
    setMenu(next.kind === "space" ? "model" : null);
  };

  return (
    <div
      className="flex flex-col gap-2 rounded-app border border-border"
      style={{ padding: "var(--spacing-md)" }}
      role="group"
      aria-label="Add tables"
    >
      <div className="grid grid-cols-2 gap-2">
        <PickerTrigger
          ref={sourceRef}
          open={menu === "source"}
          onToggle={() => setMenu(menu === "source" ? null : "source")}
          lead={
            space ? (
              <SpaceDot color={spaceColor(space)} />
            ) : (
              fromDesign && (
                <WidgetsOutlinedIcon
                  sx={{ fontSize: 14 }}
                  className="text-fg-muted"
                />
              )
            )
          }
          label={
            space?.name ?? designs.find((d) => d.app_id === source?.id)?.name
          }
          placeholder="Pick a space or an app"
        />
        <PickerTrigger
          ref={modelRef}
          open={menu === "model"}
          onToggle={() => setMenu(menu === "model" ? null : "model")}
          disabled={!source || fromDesign}
          lead={modelOf && <ModelMark model={modelOf} />}
          label={modelOf?.name}
          placeholder={fromDesign ? "The design's model" : "Pick a model"}
          trail={
            fromDesign && (
              <LockOutlinedIcon
                sx={{ fontSize: 14 }}
                className="text-fg-muted"
              />
            )
          }
        />
      </div>
      <DropdownMenu
        anchorRef={sourceRef}
        isOpen={menu === "source"}
        onClose={() => setMenu(null)}
        className="py-1"
      >
        {menuHead("Spaces")}
        {spaces.map((s) => (
          <DropdownMenuItem
            key={s.space_id}
            isSelected={source?.id === s.space_id}
            onClick={() => pickSource({ kind: "space", id: s.space_id })}
          >
            <span className="flex w-full min-w-0 items-center gap-2">
              <SpaceDot color={spaceColor(s)} />
              <span className="flex-1 truncate">{s.name}</span>
            </span>
          </DropdownMenuItem>
        ))}
        {designs.length > 0 && menuHead("Design apps")}
        {designs.map((d) => (
          <DropdownMenuItem
            key={d.app_id}
            isSelected={source?.id === d.app_id}
            disabled={!d.applied_version}
            hint={d.applied_version ? undefined : "not applied"}
            onClick={() => pickSource({ kind: "design", id: d.app_id })}
          >
            <span className="flex w-full min-w-0 items-center gap-2">
              <WidgetsOutlinedIcon
                sx={{ fontSize: 14 }}
                className="text-fg-muted"
              />
              <span className="flex-1 truncate">{d.name}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
      <ModelListMenu
        anchorRef={modelRef}
        isOpen={menu === "model"}
        onClose={() => setMenu(null)}
        models={models.filter((m) => m.space_id === source?.id)}
        selected={modelId}
        onPick={(id) => {
          setModelId(id);
          setTicks({});
          setMenu(null);
        }}
      />
      {model && (
        <ul
          role="group"
          aria-label="Tables to add"
          className="flex flex-col rounded-app border border-border bg-bg-subtle m-0 p-1"
        >
          {rows.length === 0 && (
            <li className="list-none px-2 text-fg-muted-regular-small">
              {tables ? "This model has no tables yet" : "Reading the tables…"}
            </li>
          )}
          {rows.map((t) => {
            const on = ticked(t.table_id);
            return (
              <li key={t.table_id} className="list-none">
                <TableCheckRow
                  on={on}
                  disabled={isLent(t.table_id)}
                  onToggle={() =>
                    setTicks((k) => ({ ...k, [t.table_id]: !on }))
                  }
                  name={t.name}
                  fact={isLent(t.table_id) ? "lent" : ""}
                />
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-fg-muted-regular-small flex-1">
          {!model
            ? "Pick where the tables are"
            : fromDesign
              ? "Lent to write: the design made them to be filled"
              : "Lent to read · switch any to write"}
        </span>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          disabled={adding.length === 0}
          onClick={() =>
            onAdd(
              adding.map((t) => ({
                table_id: t.table_id,
                name: t.name,
                model: modelOf?.name ?? null,
                access: fromDesign ? "write" : "read",
              })),
            )
          }
        >
          {adding.length ? `Add ${plural(adding.length, "table")}` : "Add"}
        </Button>
      </div>
    </div>
  );
}

/** The lent tables, each lent to read or to write. */
export function LentList({
  lent,
  onChange,
}: {
  lent: LentTable[];
  onChange: (lent: LentTable[]) => void;
}) {
  return (
    <ul className="flex flex-col m-0 p-0" aria-label="Lent tables">
      {lent.map((t) => (
        <li
          key={t.table_id}
          className="list-none flex items-center gap-2 border-b border-border"
          style={{ minHeight: "2.5rem" }}
        >
          <TableChartOutlinedIcon
            sx={{ fontSize: 14 }}
            className="text-fg-muted"
          />
          <span className="text-fg font-medium truncate">{t.name}</span>
          <span className="text-fg-muted-regular-small flex-1 truncate">
            {t.model}
          </span>
          <SegmentedControl<LendAccess>
            value={t.access}
            onChange={(access) =>
              onChange(
                lent.map((x) =>
                  x.table_id === t.table_id ? { ...x, access } : x,
                ),
              )
            }
            options={[
              { value: "read", label: "Read" },
              { value: "write", label: "Write" },
            ]}
          />
          <TooltipButton
            title={`Stop lending ${t.name}`}
            aria-label={`Stop lending ${t.name}`}
            variant="default"
            size="xs"
            onClick={() =>
              onChange(lent.filter((x) => x.table_id !== t.table_id))
            }
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </TooltipButton>
        </li>
      ))}
    </ul>
  );
}

/** The lent tables and the tables they link to, which are drawn dashed. */
export function lentEmbedModel(
  lent: LentTable[],
  links: TableLink[],
): EmbedModel {
  const ids = new Set(lent.map((t) => t.table_id));
  const seen = new Set<string>();
  const touching = links.filter((l) => {
    const near =
      ids.has(l.first_item.table_id) || ids.has(l.second_item.table_id);
    if (!near || seen.has(l.id)) return false;
    seen.add(l.id);
    return true;
  });
  const tables = new Map<string, EmbedModel["tables"][number]>(
    lent.map((t) => [
      t.table_id,
      { id: t.table_id, name: t.name, kind: "mine", columns: [] },
    ]),
  );
  const columnOf = (end: TableLink["first_item"]) => {
    const table =
      tables.get(end.table_id) ??
      tables
        .set(end.table_id, {
          id: end.table_id,
          name: end.table_name ?? "Linked table",
          kind: "public",
          columns: [],
        })
        .get(end.table_id)!;
    const name = end.column_names?.[0] ?? "";
    if (!table.columns.some((c) => c.name === name))
      table.columns.push({ name });
    return name;
  };
  const drawn = touching.map((l) => ({
    from: { table: l.first_item.table_id, column: columnOf(l.first_item) },
    to: { table: l.second_item.table_id, column: columnOf(l.second_item) },
  }));
  return { tables: [...tables.values()], links: drawn };
}

/** The graph of the lent tables; a dashed table, linked but not lent, is
 *  lent to read on a click. */
export function LentGraph({
  lent,
  onLend,
}: {
  lent: LentTable[];
  onLend: (table: LentTable) => void;
}) {
  const links = useTableLinks(lent.map((t) => t.table_id));
  const model = lentEmbedModel(lent, links);
  return (
    <DraftGraph
      model={model}
      highlighted={null}
      selected={null}
      onPick={(id) => {
        const table = model.tables.find((t) => t.id === id);
        if (table?.kind === "public")
          onLend({
            table_id: table.id,
            name: table.name,
            model: null,
            access: "read",
          });
      }}
    />
  );
}
